
import React, { useState, useEffect } from 'react';
import httpHelper from 'utils/http-helper';
import URL_CONSTANTS from 'constants/url-constants';
import { Method_TYPE } from 'utils/http-helper';
import DataTable from 'react-data-table-component';
import ReportHeader from './ReportHeader';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { ArrowUpDown } from 'lucide-react';
import { convertToIST } from 'utils/formtaDate';
import {
  MEMBER_SORT_OPTIONS,
  SEVADAL_SEARCH_FIELDS,
  useFilteredSortedData,
  makeSortableColumn,
  makePlainColumn,
  ReportTableControls,
  ExportButtons,
  createColumnSortHandler,
  reportTableStyles,
} from './reportTableUtils';

export default function AgeGroupReport() {
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedAgeGroup, setSelectedAgeGroup] = useState('');
  const [districtValues, setDistrictValues] = useState([]);
  const [sevadalDetails, setSevadalDetails] = useState([]);
  const [tableSearchTerm, setTableSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('sector');
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    getDistrictValue();
  }, []);

  const getDistrictValue = async () => {
    try {
      const data = await httpHelper(URL_CONSTANTS.GET_DISTRICT_VALUES, Method_TYPE.GET);
      setDistrictValues(data?.data || []);
    } catch (err) {
      console.error('Error fetching district values:', err);
      setDistrictValues([]);
    }
  };

  const getAgeGroupRange = (label) => {
    switch (label) {
      case '18 to 30': return [18, 30];
      case '30 to 45': return [30, 45];
      case '45 to 60': return [45, 60];
      case '60 to 70': return [60, 70];
      case 'Above 70': return [70, Infinity];
      default: return [0, Infinity];
    }
  };

  const handleSearchSevadal = async () => {
    if (!selectedDistrict) {
      alert("Please select a district.");
      return;
    }

    try {
      const data = await httpHelper(`${URL_CONSTANTS.GET_SEVADAL_DETAILS}?district=${selectedDistrict}`, Method_TYPE.GET);
      let formattedData = data?.reports?.sevaDalList?.map((tableData) => ({
        name: tableData?.Name,
        age: tableData?.age,
        district: tableData?.district,
        samithi: tableData?.samithi,
        mobile: tableData?.mobNo,
        saiconnectedid: tableData?.saiOneId,
        sector: tableData?.Sector,
        deploymentpoint: tableData?.DutyPoint,
      })) || [];

      if (selectedAgeGroup) {
        const [min, max] = getAgeGroupRange(selectedAgeGroup);
        formattedData = formattedData.filter(item => {
          const age = parseInt(item.age, 10);
          return !isNaN(age) && age >= min && age <= max;
        });
      }

      setSevadalDetails(formattedData);
    } catch (err) {
      console.error('Error fetching Sevadal details:', err);
      setSevadalDetails([]);
    }
  };

  const handleColumnSort = createColumnSortHandler(sortBy, setSortBy, sortDirection, setSortDirection);

  const filteredAndSortedData = useFilteredSortedData(sevadalDetails, {
    searchTerm: tableSearchTerm,
    searchableFields: SEVADAL_SEARCH_FIELDS,
    sortBy,
    sortDirection,
  });

  const handlePrint = () => {
    const printContent = document.getElementById('print-table').innerHTML;
    const printWindow = window.open('', '', 'height=600,width=800');
    printWindow.document.write('<html><head><title>Age Group Report</title></head><body>');
    printWindow.document.write(`<h2>TamilNadu Prasanthi Seva - Age Group Report</h2><p><strong>${getBatchInfo()}</strong></p>`);
    printWindow.document.write(printContent);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const getBatchInfo = () => {
    const batchData = JSON.parse(localStorage.getItem('currentBatch'));
    if (batchData) {
      return `${batchData.name}:${convertToIST(batchData.startDate)} - ${convertToIST(batchData.endDate)}`;
    }
    return '';
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    doc.text('Age Group Report', 14, 10);
    doc.text(`${getBatchInfo()}`, 14, 18);
    autoTable(doc, {
      startY: 20,
      head: [['Sl.No', 'Name', 'Age', 'District', 'Samithi', 'Mobile', 'Sai Connect', 'Sector', 'Deployment Point']],
      body: filteredAndSortedData.map((row, index) => [
        index + 1,
        row.name, row.age, row.district, row.samithi, row.mobile,
        row.saiconnectedid, row.sector, row.deploymentpoint,
      ]),
    });
    doc.save('age-group-report.pdf');
  };

  const handleDownloadXLS = () => {
    const exportData = filteredAndSortedData.map((item, index) => ({
      'Sl.No': index + 1,
      Name: item.name,
      Age: item.age,
      District: item.district,
      Samithi: item.samithi,
      Mobile: item.mobile,
      'Sai Connect': item.saiconnectedid,
      Sector: item.sector,
      'Deployment Point': item.deploymentpoint,
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'AgeGroup');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(data, 'age-group-report.xlsx');
  };

  const columns = [
    {
      name: 'Sl.No',
      selector: (row, index) => index + 1,
      sortable: false,
      width: '80px',
    },
    makeSortableColumn('Name', 'name', sortBy, sortDirection, handleColumnSort),
    makeSortableColumn('Age', 'age', sortBy, sortDirection, handleColumnSort),
    makeSortableColumn('District', 'district', sortBy, sortDirection, handleColumnSort),
    makeSortableColumn('Samithi', 'samithi', sortBy, sortDirection, handleColumnSort),
    makePlainColumn('Mobile', 'mobile'),
    makePlainColumn('Sai Connect', 'saiconnectedid'),
    makeSortableColumn('Sector', 'sector', sortBy, sortDirection, handleColumnSort),
    makePlainColumn('Deployment Point', 'deploymentpoint'),
  ];

  const ageGroups = ['18 to 30', '30 to 45', '45 to 60', '60 to 70', 'Above 70'];

  return (
    <>
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <div className="flex gap-2 items-center">
          <label className='font-bold text-sm'>District:</label>
          <select
            value={selectedDistrict}
            onChange={(e) => {
              setSelectedDistrict(e.target.value);
              setSevadalDetails([]);
            }}
            className="w-[250px] rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
          >
            <option value="">Select District</option>
            {districtValues.map((d) => (
              <option key={d?._id} value={d?._id}>
                {d?.districtName}
              </option>
            ))}
          </select>
        </div>

        <div className="flex gap-2 items-center">
          <label className='font-bold text-sm'>Age Group:</label>
          <select
            value={selectedAgeGroup}
            onChange={(e) => setSelectedAgeGroup(e.target.value)}
            className="w-[200px] rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
          >
            <option value="">Select Age Group</option>
            {ageGroups.map((group, index) => (
              <option key={index} value={group}>{group}</option>
            ))}
          </select>
        </div>

        <button
          type="button"
          onClick={handleSearchSevadal}
          className="bg-primary text-primary-foreground text-white px-4 py-2 rounded hover:bg-accent">
          Search
        </button>
      </div>

      <ReportTableControls
        searchTerm={tableSearchTerm}
        onSearchChange={setTableSearchTerm}
        searchPlaceholder="Search name, age, district, samithi, sector..."
        sortBy={sortBy}
        onSortByChange={(value) => {
          setSortBy(value);
          setSortDirection('asc');
        }}
        sortDirection={sortDirection}
        onSortDirectionChange={setSortDirection}
        sortOptions={MEMBER_SORT_OPTIONS}
        totalCount={sevadalDetails.length}
        filteredCount={filteredAndSortedData.length}
        actions={
          <ExportButtons
            show={sevadalDetails.length > 0}
            onPrint={handlePrint}
            onPdf={handleDownloadPDF}
            onXls={handleDownloadXLS}
          />
        }
        hint={
          <>
            Click column headers with{' '}
            <ArrowUpDown size={12} className="inline mx-0.5 text-slate-400" /> to sort by Name, Age, District,
            Samithi, or Sector
          </>
        }
      />

      <ReportHeader header='Age Group Report' />
      <DataTable
        columns={columns}
        data={filteredAndSortedData}
        fixedHeader
        fixedHeaderScrollHeight='500px'
        className='reports-table'
        customStyles={reportTableStyles}
        noDataComponent={
          <div className="py-10 text-sm text-slate-500">
            {tableSearchTerm.trim() ? 'No rows match your search' : 'Load data using district and Search above'}
          </div>
        }
      />

      <div id="print-table" style={{ display: 'none' }}>
        <table border="1" cellPadding="5" cellSpacing="0" width="100%">
          <thead>
            <tr>
              <th>Sl.no</th>
              <th>Name</th>
              <th>Age</th>
              <th>District</th>
              <th>Samithi</th>
              <th>Mobile</th>
              <th>Sai Connect</th>
              <th>Sector</th>
              <th>Deployment Point</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedData.map((item, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td>{item.name}</td>
                <td>{item.age}</td>
                <td>{item.district}</td>
                <td>{item.samithi}</td>
                <td>{item.mobile}</td>
                <td>{item.saiconnectedid}</td>
                <td>{item.sector}</td>
                <td>{item.deploymentpoint}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
